import { useEffect, useState } from "react";
import { Loader2, Receipt, X, Wallet, Camera, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, getMe } from "../lib/api";
import * as guestStore from "../lib/guestStorage";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";

const CURRENCIES = ["USD", "EUR", "CUP"];
const SYMBOL = { USD: "$", EUR: "€", CUP: "₱" };

/**
 * LogSpendModal — opens to either create a new spend entry, or edit/delete an existing one.
 * Props:
 *   open: bool
 *   onOpenChange: fn
 *   targetType: "business" | "vendor"
 *   targetId: string
 *   targetName: string
 *   entry: existing entry (omit for create)
 *   onSaved: callback fired after successful save/delete
 */
export default function LogSpendModal({
  open, onOpenChange, targetType, targetId, targetName, entry, onSaved,
}) {
  const isEdit = !!entry;
  const today = new Date().toISOString().slice(0, 10);
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [date, setDate] = useState(today);
  const [note, setNote] = useState("");
  const [receiptUrl, setReceiptUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (entry) {
      setAmount(String(entry.amount ?? ""));
      setCurrency(entry.currency || "USD");
      setDate(entry.date || today);
      setNote(entry.note || "");
      setReceiptUrl(entry.receipt_url || null);
    } else {
      setAmount("");
      setCurrency("USD");
      setDate(today);
      setNote("");
      setReceiptUrl(null);
    }
  }, [open, entry]); // eslint-disable-line

  const handleReceipt = async (file) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.error("Receipt must be under 15 MB");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/upload/image", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setReceiptUrl(data.medium || data.full || data.thumbnail);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't upload receipt");
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) {
      toast.error("Enter an amount");
      return;
    }
    setSaving(true);
    try {
      // Detect auth: try to update via /me/spending; if guest, fall back to localStorage.
      const me = await getMe().catch(() => null);
      if (!me) {
        // Guest path
        if (isEdit) {
          guestStore.updateSpend(entry.id, { amount: amt, currency, note: note || null, receipt_url: receiptUrl, date });
          toast.success("Spend updated");
        } else {
          guestStore.appendSpend({
            target_type: targetType, target_id: targetId, amount: amt, currency,
            note: note || null, receipt_url: receiptUrl, date,
          });
          toast.success("Spend logged");
        }
        onSaved?.();
        onOpenChange(false);
        return;
      }
      if (isEdit) {
        await api.patch(`/me/spending/${entry.id}`, {
          amount: amt,
          currency,
          note: note || null,
          receipt_url: receiptUrl,
          date,
        });
        toast.success("Spend updated");
      } else {
        await api.post("/me/spending", {
          target_type: targetType,
          target_id: targetId,
          amount: amt,
          currency,
          note: note || null,
          receipt_url: receiptUrl,
          date,
        });
        toast.success("Spend logged");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!entry) return;
    if (!window.confirm("Delete this spend entry?")) return;
    setDeleting(true);
    try {
      // Guest-aware delete
      const me = await getMe().catch(() => null);
      if (!me) {
        guestStore.deleteSpend(entry.id);
        toast.success("Spend deleted");
        onSaved?.();
        onOpenChange(false);
        return;
      }
      await api.delete(`/me/spending/${entry.id}`);
      toast.success("Spend deleted");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't delete");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="log-spend-modal"
        className="border border-gold-strong p-0 max-w-md w-[92vw] rounded-none"
        style={{ background: "#171717" }}
      >
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-gold-soft">
          <DialogTitle className="font-display text-2xl text-foreground flex items-center gap-2">
            <Wallet className="w-5 h-5 text-gold" strokeWidth={1.5} />
            {isEdit ? "Edit spend" : "Log spend"}
          </DialogTitle>
          <DialogDescription className="text-xs text-foreground/55 mt-1">
            {targetName} · Private to you. Never shared without permission.
          </DialogDescription>
        </DialogHeader>

        <div className="px-5 py-5 space-y-4">
          {/* Amount + currency */}
          <div className="grid grid-cols-[1fr_110px] gap-2">
            <label className="block">
              <div className="eyebrow mb-1">Amount</div>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gold font-display text-lg pointer-events-none">
                  {SYMBOL[currency]}
                </span>
                <input
                  data-testid="spend-amount-input"
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="0.01"
                  className="form-input pl-8"
                  style={{ minHeight: 48 }}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  autoFocus
                />
              </div>
            </label>
            <label className="block">
              <div className="eyebrow mb-1">Currency</div>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger data-testid="spend-currency-select" className="form-input" style={{ minHeight: 48 }}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border border-gold-soft" style={{ background: "#171717" }}>
                  {CURRENCIES.map((c) => (
                    <SelectItem key={c} value={c} data-testid={`spend-currency-${c}`}>
                      {SYMBOL[c]} {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>
          </div>

          {/* Date */}
          <label className="block">
            <div className="eyebrow mb-1">Date</div>
            <input
              data-testid="spend-date-input"
              type="date"
              className="form-input"
              style={{ minHeight: 48 }}
              value={date}
              max={today}
              onChange={(e) => setDate(e.target.value)}
            />
          </label>

          {/* Note */}
          <label className="block">
            <div className="eyebrow mb-1">Notes <span className="opacity-50 normal-case tracking-normal">— optional</span></div>
            <textarea
              data-testid="spend-note-input"
              className="form-input"
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Two cigars, mojito, tip…"
            />
          </label>

          {/* Receipt */}
          <div className="block">
            <div className="eyebrow mb-2">Receipt <span className="opacity-50 normal-case tracking-normal">— optional</span></div>
            {receiptUrl ? (
              <div className="relative inline-block">
                <img
                  src={receiptUrl}
                  alt="Receipt"
                  className="w-24 h-24 object-cover border border-gold-soft rounded-sm"
                  data-testid="spend-receipt-preview"
                />
                <button
                  data-testid="spend-receipt-remove"
                  type="button"
                  onClick={() => setReceiptUrl(null)}
                  className="absolute -top-2 -right-2 w-7 h-7 grid place-items-center rounded-full bg-background border border-gold-soft text-foreground/80 hover:text-foreground"
                >
                  <X className="w-3.5 h-3.5" strokeWidth={1.5} />
                </button>
              </div>
            ) : (
              <label
                data-testid="spend-receipt-upload"
                className="inline-flex items-center gap-2 px-4 py-3 border border-dashed border-gold-soft rounded-sm cursor-pointer hover:border-gold text-xs text-foreground/70"
                style={{ minHeight: 48 }}
              >
                {uploading ? (
                  <Loader2 className="w-4 h-4 animate-spin text-gold" strokeWidth={1.5} />
                ) : (
                  <Camera className="w-4 h-4 text-gold" strokeWidth={1.5} />
                )}
                {uploading ? "Uploading…" : "Add a photo"}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleReceipt(e.target.files?.[0])}
                />
              </label>
            )}
          </div>
        </div>

        <div className="px-5 py-4 border-t border-gold-soft flex items-center justify-between gap-2">
          {isEdit ? (
            <button
              data-testid="spend-delete-btn"
              type="button"
              onClick={handleDelete}
              disabled={deleting || saving}
              className="btn-ghost text-xs text-red-400 hover:text-red-300"
              style={{ minHeight: 48 }}
            >
              <Trash2 className="w-4 h-4" strokeWidth={1.5} />
              Delete
            </button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <button
              data-testid="spend-cancel-btn"
              type="button"
              onClick={() => onOpenChange(false)}
              className="btn-outline text-xs"
              style={{ minHeight: 48 }}
            >
              Cancel
            </button>
            <button
              data-testid="spend-save-btn"
              type="button"
              onClick={handleSave}
              disabled={saving || uploading}
              className="btn-gold text-xs"
              style={{ minHeight: 48 }}
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> : <Receipt className="w-4 h-4" strokeWidth={1.5} />}
              {isEdit ? "Save changes" : "Log spend"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export { CURRENCIES, SYMBOL };
